import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, inr, resolveImg, formatApiErrorDetail } from "../lib/api";
import { ArrowLeft, CheckCircle2, ShieldAlert, Copy, Check, Clock, XCircle, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  active: { label: "Active", cls: "bg-sage/15 text-sage" },
  due: { label: "Due", cls: "bg-ochre/15 text-ochre" },
  expired: { label: "Expired", cls: "bg-destructive/10 text-destructive" },
};

export default function SellerSubscriptionPage() {
  const { user, loading } = useAuth();
  const [info, setInfo] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [utr, setUtr] = useState("");
  const [screenshotUrl, setScreenshotUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/seller/subscription").then((r) => setInfo(r.data));
  useEffect(() => { if (user?.role === "seller") load(); }, [user]);

  if (loading || user === null) return <div className="container-x py-16 text-charcoal-muted">Loading…</div>;
  if (!user) return <Navigate to="/seller/login" replace />;
  if (user.role !== "seller") return <Navigate to="/" replace />;
  if (!info) return <div className="container-x py-16 text-charcoal-muted">Loading…</div>;

  const meta = STATUS_META[info.subscription_status] || STATUS_META.due;
  const upi = info.upi || {};
  const req = info.latest_request;
  const pending = req && req.status === "pending";
  const rejected = req && req.status === "rejected";

  const expiryDate = info.subscription_expires_at
    ? new Date(info.subscription_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "—";

  const copyUpi = async () => {
    const text = upi.upi_id || "";
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("UPI ID copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy");
    }
  };

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScreenshotUrl(data.url);
      toast.success("Screenshot attached");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!utr.trim() || utr.trim().length < 4) { toast.error("Enter a valid transaction reference (UTR)"); return; }
    setBusy(true);
    try {
      await api.post("/seller/subscription/manual-request", { utr: utr.trim(), screenshot_url: screenshotUrl || null });
      toast.success("Payment submitted — pending verification");
      setShowForm(false);
      setUtr("");
      setScreenshotUrl("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-x py-8 md:py-12 max-w-3xl" data-testid="subscription-page">
      <Link to="/seller/dashboard" className="inline-flex items-center gap-1 text-sm text-charcoal-muted hover:text-terracotta mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <div className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold mb-2">Seller Subscription</div>
      <h1 className="font-heading font-bold text-3xl md:text-4xl text-charcoal mb-6">Keep your store live</h1>

      <div className="bg-white border border-border p-6 md:p-8">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-1">Current status</div>
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs uppercase tracking-widest font-semibold px-2 py-0.5 ${meta.cls}`} data-testid="subscription-status">{meta.label}</span>
              {info.subscription_status === "active" && <span className="text-xs text-charcoal-muted">· {info.subscription_days_left} days left</span>}
            </div>
            <div className="text-sm text-charcoal-muted">
              {info.subscription_status === "active" ? `Renews / expires on ${expiryDate}` : "Renew now to keep your products visible to buyers."}
            </div>
          </div>
          {info.subscription_status !== "active" && <ShieldAlert className="w-10 h-10 text-ochre" />}
          {info.subscription_status === "active" && <CheckCircle2 className="w-10 h-10 text-sage" />}
        </div>

        <div className="grid md:grid-cols-3 gap-4 my-6">
          <Info label="Amount to pay" value={inr(info.price)} />
          <Info label="Period" value={`${info.period_days} days`} />
          <Info label="Method" value="UPI (manual verify)" />
        </div>
      </div>

      {/* Manual UPI payment block */}
      <div className="bg-white border border-border p-6 md:p-8 mt-6" data-testid="upi-payment-block">
        <h2 className="font-heading font-bold text-xl text-charcoal mb-1">Pay via UPI</h2>
        <p className="text-sm text-charcoal-muted mb-5">
          Pay <span className="font-semibold text-charcoal">{inr(info.price)}</span> to the UPI ID below (or scan the QR),
          then click “I've Paid” and enter your UPI transaction reference number (UTR).
        </p>

        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start">
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-1">UPI ID</div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-semibold text-charcoal text-lg" data-testid="upi-id">{upi.upi_id}</span>
                <button onClick={copyUpi} data-testid="copy-upi-btn" className="inline-flex items-center gap-1 text-xs border border-border hover:border-terracotta text-charcoal px-2 py-1 transition-colors">
                  {copied ? <><Check className="w-3.5 h-3.5 text-sage" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-1">Account holder</div>
              <div className="font-heading font-semibold text-charcoal" data-testid="upi-holder">{upi.holder_name}</div>
            </div>
          </div>

          {upi.qr_url && (
            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-2">Scan QR to pay</div>
              <img
                src={resolveImg(upi.qr_url)}
                alt="UPI QR code"
                data-testid="upi-qr-img"
                className="w-44 h-44 object-contain border border-border bg-off-white-alt p-2"
              />
            </div>
          )}
        </div>

        <div className="border-t border-border mt-6 pt-6">
          {pending ? (
            <div className="bg-ochre/10 border border-ochre/40 p-4 flex items-start gap-3" data-testid="pending-request-card">
              <Clock className="w-5 h-5 text-ochre shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold text-charcoal">Payment submitted — pending verification</div>
                <div className="text-charcoal-muted mt-1">
                  UTR: <span className="font-medium text-charcoal">{req.utr}</span> · Submitted on{" "}
                  {new Date(req.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
                <div className="text-charcoal-muted mt-1">Our team will verify and activate your subscription shortly.</div>
              </div>
            </div>
          ) : (
            <>
              {rejected && (
                <div className="bg-destructive/10 border border-destructive/30 p-3 flex items-start gap-2 mb-4 text-sm" data-testid="rejected-request-card">
                  <XCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                  <div className="text-charcoal-muted">
                    Your previous request (UTR {req.utr}) was rejected. Please re-check the payment and submit again.
                  </div>
                </div>
              )}

              {!showForm ? (
                <button onClick={() => setShowForm(true)} data-testid="ive-paid-btn" className="btn-terracotta w-full md:w-auto">
                  I've Paid
                </button>
              ) : (
                <div className="space-y-4" data-testid="payment-form">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-charcoal-muted font-semibold mb-1">Transaction reference / UTR *</label>
                    <input
                      data-testid="utr-input"
                      value={utr}
                      onChange={(e) => setUtr(e.target.value)}
                      placeholder="e.g. 431298765432"
                      className="w-full border-2 border-border focus:border-terracotta bg-off-white px-3 py-2 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] text-charcoal-muted font-semibold mb-1">Payment screenshot (optional)</label>
                    <label className="inline-flex items-center gap-2 text-sm border border-border hover:border-terracotta px-3 py-2 cursor-pointer transition-colors" data-testid="screenshot-upload-label">
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {uploading ? "Uploading…" : screenshotUrl ? "Replace screenshot" : "Upload screenshot"}
                      <input type="file" accept="image/*" className="hidden" data-testid="screenshot-input"
                        onChange={(e) => doUpload(e.target.files?.[0])} />
                    </label>
                    {screenshotUrl && (
                      <img src={resolveImg(screenshotUrl)} alt="payment" className="mt-2 w-24 h-24 object-cover border border-border" data-testid="screenshot-preview" />
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <button onClick={submit} disabled={busy || uploading} data-testid="submit-payment-btn" className="btn-terracotta">
                      {busy ? "Submitting…" : "Submit for verification"}
                    </button>
                    <button onClick={() => { setShowForm(false); setUtr(""); setScreenshotUrl(""); }} className="text-sm text-charcoal-muted hover:text-charcoal" data-testid="cancel-payment-btn">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <p className="text-[11px] text-charcoal-muted mt-4">
          Subscription amount is set by TerraZoon admin and can change. On verification, {info.period_days} days are added on top of any remaining time.
        </p>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="border border-border p-4 bg-off-white-alt">
      <div className="text-xs uppercase tracking-[0.2em] text-charcoal-muted mb-1">{label}</div>
      <div className="font-heading font-semibold text-charcoal">{value}</div>
    </div>
  );
}
